/**
 * P2.1 — the hymn detail page, one per hymn (720 routes).
 *
 * Renders every field the dataset holds for a hymn and nothing it does not.
 * NO LYRICS AND NO NOTATION (plan D7): the Hymnal 1982's texts, translations and
 * harmonizations are Church Publishing's copyright, and factual metadata is what
 * makes this page publishable at all.
 *
 * Sections that would be empty are omitted rather than rendered blank — the
 * discipline lifted from Great Host of Composers, whose ReadingsLink renders
 * nothing rather than a dead link.
 */
import { Link, useParams } from 'react-router-dom'
import type { PersonRole } from '@gjl/music-manager/hymnal-pages'

import { index, HYMNAL_1982 } from '@/data/hymnal'
import { creditsFor, relatedByMeter, roleLabel } from '@/lib/hymnal'
import { Crumb } from '@/components/HymnList'
import { NotFound } from '@/components/NotFound'
import { PageHead } from '@/components/PageHead'
import { CanticleCta } from '@/components/CanticleCta'

const hymnalPath = (rest: string) => `/hymnal/${HYMNAL_1982.id}${rest}`

/** A labelled definition row. Renders nothing when the value is absent, so the
 *  metadata block never shows an empty label. */
function Row({ label, children }: { label: string; children?: React.ReactNode }) {
  if (!children) return null
  return (
    <div className="grid grid-cols-[7.5rem_1fr] gap-x-4 gap-y-1 py-1.5 border-b border-border last:border-0">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground pt-0.5">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  )
}

/**
 * A credited person, linked ONLY when that person actually has a page.
 *
 * ⚠️ THIS GUARD IS THE FIX FOR A REAL DEFECT, not defensive decoration. Until
 * 2026-08-09 both credit blocks below linked every name unconditionally while
 * person pages were gated at >=3 hymns, so **619 credit instances across the
 * corpus resolved to the app's catch-all**. It was invisible to the prerenderer —
 * these are client-side router links, so all 968 prerendered files were fine and
 * nothing failed the build.
 *
 * The threshold is now 1, so today every credit resolves. The guard stays anyway:
 * it is what makes raising the threshold a display change instead of a silent
 * 404 farm, and it matches what the Tune and Metre rows on this page already do.
 * `hymnal-pages.test.ts` holds the corresponding model-level assertion.
 */
function Credit({ role, name }: { role: PersonRole; name: string }) {
  const slug = slugOf(name)
  const hasPage = index.personBySlug.has(slug)
  return (
    <Row label={roleLabel(role)}>
      {hasPage ? (
        <Link
          to={hymnalPath(`/person/${slug}`)}
          className="underline decoration-border underline-offset-4 hover:decoration-foreground"
        >
          {name}
        </Link>
      ) : (
        <span>{name}</span>
      )}
    </Row>
  )
}

export default function HymnPage() {
  const { number } = useParams<{ number: string }>()
  const hymn = number ? index.hymnBySlug.get(number) : undefined

  if (!hymn) {
    return (
      <NotFound
        title="No such hymn"
        detail={`The Hymnal 1982 has no hymn numbered ${number ?? '(none)'}.`}
        backTo={hymnalPath('')}
        backLabel="Browse all 720 hymns"
      />
    )
  }

  const credits = creditsFor(hymn)
  const otherTunes = relatedByMeter(index, hymn)
  const tune = hymn.tuneSlug ? index.tuneBySlug.get(hymn.tuneSlug) : undefined
  const meterHasPage = hymn.meterSlug ? index.meterBySlug.has(hymn.meterSlug) : false
  const section = hymn.sectionSlug ? index.sectionBySlug.get(hymn.sectionSlug) : undefined

  return (
    <article className="mx-auto max-w-3xl px-5 py-12">
      <PageHead
        title={`${hymn.firstLine} — Hymn ${hymn.number}`}
        description={`Hymn ${hymn.number} in The Hymnal 1982${hymn.tuneName ? `, sung to ${hymn.tuneName}` : ''}${hymn.meter ? ` (${hymn.meter})` : ''}. Text, tune, meter and credits.`}
        path={hymnalPath(`/hymn/${hymn.slug}`)}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'MusicComposition',
          name: hymn.firstLine,
          ...(hymn.author ? { lyricist: { '@type': 'Person', name: hymn.author } } : {}),
          ...(hymn.composer ? { composer: { '@type': 'Person', name: hymn.composer } } : {}),
          ...(hymn.language ? { inLanguage: hymn.language } : {}),
          isPartOf: { '@type': 'MusicAlbum', name: HYMNAL_1982.label },
        }}
      />

      {/* Was a hand-rolled copy of <Crumb>, and its hover state read
          `hover:text-muted-foreground` — the colour it already was. */}
      <Crumb label={`Hymn ${hymn.number}`} />

      <header className="mb-8">
        <p className="text-sm font-mono text-muted-foreground mb-2">Hymn {hymn.number}</p>
        <h1 className="text-3xl sm:text-4xl font-display leading-tight text-foreground">{hymn.firstLine}</h1>
      </header>

      <section aria-labelledby="text-h" className="mb-8">
        <h2 id="text-h" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
          Text
        </h2>
        <dl>
          {credits
            .filter((c) => c.role === 'author' || c.role === 'translator')
            .map((c) => (
              <Credit key={c.role} role={c.role} name={c.name} />
            ))}
          <Row label="Meter">{hymn.textMeter}</Row>
          <Row label="Language">{hymn.language}</Row>
          <Row label="Published">{hymn.publicationDate}</Row>
          {/* Where the book itself puts this hymn — the single most useful fact
              for a musician planning a service, and the one the dataset never
              carried. Season is named only where the section IS one. */}
          <Row label={section?.season ? 'Season' : 'Section'}>
            {section && (
              <Link
                to={hymnalPath(`/section/${section.slug}`)}
                className="underline decoration-border underline-offset-4 hover:decoration-foreground"
              >
                {section.label}
              </Link>
            )}
          </Row>
        </dl>
      </section>

      <section aria-labelledby="tune-h" className="mb-8">
        <h2 id="tune-h" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">
          Tune
        </h2>
        <dl>
          <Row label="Name">
            {tune ? (
              <Link
                to={hymnalPath(`/tune/${tune.slug}`)}
                className="font-medium tracking-wide underline decoration-border underline-offset-4 hover:decoration-foreground"
              >
                {hymn.tuneName}
              </Link>
            ) : (
              <span className="font-medium tracking-wide">{hymn.tuneName}</span>
            )}
          </Row>
          {credits
            .filter((c) => c.role === 'composer' || c.role === 'arranger' || c.role === 'harmonizer')
            .map((c) => (
              <Credit key={c.role} role={c.role} name={c.name} />
            ))}
          <Row label="Meter">
            {hymn.meter && meterHasPage ? (
              <Link
                to={hymnalPath(`/meter/${hymn.meterSlug}`)}
                className="underline decoration-border underline-offset-4 hover:decoration-foreground"
              >
                {hymn.meter}
              </Link>
            ) : (
              hymn.meter
            )}
          </Row>
          <Row label="Key">{hymn.key}</Row>
        </dl>
      </section>

      {otherTunes.length > 0 && (
        <section aria-labelledby="subs-h" className="mb-8">
          <h2 id="subs-h" className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">
            Other {hymn.meter} tunes
          </h2>
          <p className="text-sm text-muted-foreground mb-3">
            This text is {hymn.meter}, so it can be sung to any tune in the same meter.
          </p>
          <ul className="flex flex-wrap gap-2">
            {otherTunes.map((t) => (
              <li key={t.slug}>
                <Link
                  to={hymnalPath(`/tune/${t.slug}`)}
                  className="inline-block rounded border border-border px-2.5 py-1 text-xs tracking-wide text-muted-foreground hover:border-border-strong hover:text-foreground"
                >
                  {t.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <CanticleCta pageKind="hymn" pageRef={String(hymn.number)} />
    </article>
  )
}

/** Person slugs are derived the same way the model derives them. Imported rather
 *  than reimplemented so the link and the route can never disagree. */
import { personDisplayToSlugBase, slugify } from '@gjl/music-manager/hymnal-pages'
function slugOf(name: string): string {
  return slugify(personDisplayToSlugBase(name))
}
